// The catalog under the UI public-name mode (dev only, while Figma's App
// Review runs): `/auth/me` answers `publicName: true`, `fetchMe` turns the
// mode on (`setUiPublicName`, `@loom/shared/brand`), and from then on
// `translate` and `usePlural` show every catalog line with "Loom" swapped for
// `PUBLIC_PRODUCT_NAME` (`withPublicName`) — before `{vars}` are filled in, so
// a value a line carries is never touched. Off, the catalog is shown as it is.
//
// The plain swap is wrong in three kinds of line, which get a hand-written one
// here instead:
//   - a German genitive ("Looms Arbeitsbereich"; "AI-Workbenchs" is no word);
//   - a German line whose pronoun points at Loom ("es", "sein"): "Workbench"
//     is feminine in German, so a neuter pronoun reads wrong — these lines say
//     it without one;
//   - a line naming `.loom.yml`, which says "the project's settings file"
//     instead (en and de), so no "loom" shows at all.
// `test/public-name-dev.test.tsx` pins that no line shown under the mode
// matches /loom/i, and that every entry here is needed.

import { withPublicName } from "@loom/shared/brand";
import type { Locale } from "@loom/shared/config";

import { MESSAGES, type MessageKey } from "./messages/index.js";

export const PUBLIC_NAME_OVERRIDES: Readonly<
  Partial<Record<MessageKey, Readonly<Partial<Record<Locale, string>>>>>
> = {
  // German genitives.
  "breakdown.op.err.no_sandbox": {
    de: "Die Arbeitsumgebung von AI-Workbench ist gerade nicht verfügbar — versuch die Änderung gleich nochmal.",
  },
  "focus.wait.runnerLong": {
    de: "Der Arbeitsbereich von AI-Workbench ist noch nicht gestartet. Meist dauert das unter einer Minute, wenn viele Jobs anstehen aber auch mehrere. AI-Workbench macht von selbst weiter, sobald er läuft — du musst nichts tun.",
  },
  "focus.refine.noWorkspace.headline": {
    de: "Der Arbeitsbereich von AI-Workbench läuft nicht",
  },
  "focus.refine.noWorkspace.help": {
    de: "Für dieses Issue läuft gerade nichts. Das passiert, wenn der Server von AI-Workbench neu gestartet wurde oder diese Seite eine Weile weg war. Starte den Arbeitsbereich, dann macht AI-Workbench dort weiter, wo das möglich ist. Dein Issue wurde nicht verändert.",
  },
  "focus.refine.noWorkspace.start": { de: "Arbeitsbereich von AI-Workbench starten" },
  "focus.refine.stalled.reconnect": { de: "Arbeitsbereich von AI-Workbench neu starten" },
  "focus.refine.sandboxDead.headline": {
    de: "Der Arbeitsbereich von AI-Workbench wurde beendet",
  },
  "focus.refine.split.verdictStale": {
    de: "Deine Änderung hat die Must-haves verschoben, deshalb ist die Schätzung von AI-Workbench nicht mehr aktuell. Du kannst trotzdem aufteilen.",
  },
  "focus.figma.meta.fromStore": { de: "Aus der Kopie von AI-Workbench" },

  // German pronouns.
  "focus.github.connection.confirm.body": {
    de: "Deine GitHub-Projekte verschwinden aus deiner Projektliste. Arbeitet AI-Workbench gerade in einem davon, lassen sich die Änderungen dort vielleicht nicht mehr speichern.",
  },
  "focus.wait.reconnecting": {
    de: "AI-Workbench hat die Verbindung zum Arbeitsbereich verloren und verbindet neu. Klappt das nicht, zeigt dir diese Seite das und bietet einen Neustart an.",
  },
  "focus.refine.stopped.help": {
    de: "Der letzte Schritt endete ohne Frage und ohne Karte — es kommt also nichts mehr. Beginnst du neu, liest AI-Workbench das Issue und deinen Code noch einmal und fragt dich dabei vielleicht erneut, was du schon beantwortet hast. Dein Issue wurde nicht verändert.",
  },
  "focus.refine.cardStopped.help": {
    de: "Der letzte Schritt endete, bevor die Karte fertig war — es kommt also nichts mehr. Machst du weiter, arbeitet AI-Workbench an der Karte dort weiter, wo die Arbeit aufgehört hat — was du beantwortet hast, bleibt. Dein Issue wurde nicht verändert.",
  },
  "focus.refine.boot.help": {
    de: "AI-Workbench richtet sich ein, um «{title}» zu verfeinern. Dafür liest AI-Workbench deinen echten Code, damit sich die Fragen um dein Produkt drehen und nicht um Boilerplate. Alles, was AI-Workbench von dir braucht, wird direkt hier gefragt.",
  },
  "focus.refine.unverified.question": {
    de: "AI-Workbench konnte die eigene Fassung von {label} deinen Worten nicht zuordnen. Schreibst du es noch einmal?",
  },
  "focus.refine.queueHint": {
    de: "Danach noch {count} {noun} — AI-Workbench wartet auf all deine Antworten, bevor die Arbeit weitergeht.",
  },
  "focus.chat.proposal.holdBusy": {
    de: "AI-Workbench arbeitet noch — deine Antworten gehen raus, sobald die Arbeit fertig ist.",
  },
  "readiness.holdBusy": {
    de: "AI-Workbench arbeitet noch — deine Antworten gehen raus, sobald die Arbeit fertig ist.",
  },
  "focus.refine.note.receivedHelp": {
    de: "AI-Workbench berücksichtigt sie und fragt dich hier, wenn noch etwas fehlt.",
  },
  "focus.figma.allow.refused.unknownLink": {
    de: "AI-Workbench weiss nicht mehr, welche Datei hinter diesem Link steckt, darum konnte die Datei nicht erlaubt werden.",
  },

  // `.loom.yml`, all in the Figma flow.
  "focus.figma.state.notAllowedBody": {
    en: "Allowing it writes one line into the project's settings file, so everyone working on this project can use the Figma file too.",
    de: "Beim Erlauben schreibt AI-Workbench eine Zeile in die Einstellungsdatei des Projekts — dann können alle, die an diesem Projekt arbeiten, die Figma-Datei ebenfalls nutzen.",
  },
  "focus.figma.state.configMalformed": {
    en: "The Figma settings of this project can't be read, so AI-Workbench can't open Figma files here.",
    de: "Die Figma-Einstellungen dieses Projekts lassen sich nicht lesen, darum kann AI-Workbench hier keine Figma-Dateien öffnen.",
  },
  "focus.figma.state.configMalformedBody": {
    en: "Someone who looks after this project needs to fix the figma: section in the project's settings file.",
    de: "Jemand, der dieses Projekt betreut, muss den Abschnitt figma: in der Einstellungsdatei des Projekts reparieren.",
  },
  "focus.figma.state.refresh.configMalformed": {
    en: "AI-Workbench tried to read it again, but the Figma settings of this project can't be read.",
    de: "AI-Workbench wollte es noch einmal lesen, aber die Figma-Einstellungen dieses Projekts lassen sich nicht lesen.",
  },
  "focus.figma.allow.refused.writeFailed": {
    en: "AI-Workbench couldn't write this file into the project's settings file. Nothing was changed.",
    de: "AI-Workbench konnte diese Datei nicht in die Einstellungsdatei des Projekts schreiben. Es wurde nichts geändert.",
  },
  "focus.figma.allow.refused.concurrentWrite": {
    en: "Someone changed the project's settings file at the same moment, so AI-Workbench stopped. Nothing was changed.",
    de: "Jemand hat die Einstellungsdatei des Projekts im selben Moment geändert, darum hat AI-Workbench abgebrochen. Es wurde nichts geändert.",
  },
  "focus.figma.allow.refused.preconditionUnavailable": {
    en: "AI-Workbench couldn't tell which version of the project's settings file it would change, so it wrote nothing.",
    de: "AI-Workbench konnte nicht erkennen, welche Version der Einstellungsdatei des Projekts zu ändern wäre, darum wurde nichts geschrieben.",
  },
  "focus.figma.allow.refused.unavailableBody": {
    en: "Someone who looks after this project can add the file to the figma: section in the project's settings file by hand.",
    de: "Jemand, der dieses Projekt betreut, kann die Datei von Hand im Abschnitt figma: der Einstellungsdatei des Projekts eintragen.",
  },
};

/**
 * A catalog line's template under the mode. `raw` is what `translate` picked
 * (`entry[locale] ?? entry.en`), and the override follows the same fallback:
 * the locale whose text `raw` is, so a German-only override never hides the
 * English swap.
 */
export const publicNameTemplate = (
  key: MessageKey,
  locale: Locale,
  raw: string,
): string => {
  const shownLocale: Locale = MESSAGES[key]?.[locale] !== undefined ? locale : "en";
  return PUBLIC_NAME_OVERRIDES[key]?.[shownLocale] ?? withPublicName(raw);
};
